import React, { useState } from 'react';
import { Copy, CheckCircle, ExternalLink, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Owner-facing control for the public Provenance Record.
 * Lets the owner copy the shareable URL and toggle valuation disclosure.
 * Tailored for sharing with wealth managers, insurance brokers, auction houses.
 */
export const ProvenanceShareCard = ({ assetId, tokenId, initialShowValuation = false }) => {
  const [showValuation, setShowValuation] = useState(!!initialShowValuation);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = `${window.location.origin}/provenance/${assetId}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };

  const toggleValuation = async () => {
    if (saving) return;
    setSaving(true);
    const next = !showValuation;
    try {
      await axios.post(
        `${API}/tokens/${tokenId}/provenance-settings`,
        { show_valuation: next },
        { headers: { Authorization: `Bearer ${localStorage.getItem('assagart_token')}` } }
      );
      setShowValuation(next);
      toast.success(next ? 'Valuation will now be shown on the provenance record.' : 'Valuation hidden from the provenance record.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update setting.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#1a1a1a] rounded p-4 space-y-3" data-testid="provenance-share-card">
      <div>
        <p className="text-[10px] text-gold uppercase tracking-wider">Provenance Record</p>
        <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
          A read-only audit trail for wealth managers, insurance brokers, and auction houses. Direct-link only, not publicly indexed.
        </p>
      </div>

      <div className="flex gap-2 items-center">
        <code className="flex-1 px-2.5 py-2 bg-[#050505] border border-[#1a1a1a] rounded text-[10px] text-white font-mono truncate" title={url}>
          {url}
        </code>
        <button
          onClick={copy}
          className="p-2 bg-[#050505] border border-[#1a1a1a] rounded hover:border-gold/40 transition-colors"
          title="Copy link"
          data-testid="copy-provenance-link-btn"
        >
          {copied ? <CheckCircle size={14} className="text-emerald-400" /> : <Copy size={14} className="text-muted-foreground" />}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 bg-[#050505] border border-[#1a1a1a] rounded hover:border-gold/40 transition-colors"
          title="Open record"
          data-testid="open-provenance-link-btn"
        >
          <ExternalLink size={14} className="text-muted-foreground" />
        </a>
      </div>

      <button
        onClick={toggleValuation}
        disabled={saving}
        className="w-full flex items-center justify-between px-3 py-2 bg-[#050505] border border-[#1a1a1a] hover:border-gold/40 rounded transition-colors text-[11px]"
        data-testid="toggle-valuation-disclosure-btn"
      >
        <span className="text-white flex items-center gap-2">
          {showValuation ? <Eye size={12} className="text-emerald-400" /> : <EyeOff size={12} className="text-muted-foreground" />}
          Disclose valuation
        </span>
        <span className="flex items-center gap-2">
          {saving && <Loader2 size={10} className="animate-spin text-muted-foreground" />}
          <span className={`inline-flex items-center w-8 h-4 rounded-full transition-colors ${showValuation ? 'bg-gold' : 'bg-[#262626]'}`}>
            <span className={`w-3 h-3 rounded-full bg-white transform transition-transform ${showValuation ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </span>
        </span>
      </button>
      <p className="text-[10px] text-muted-foreground leading-relaxed">
        {showValuation
          ? 'Viewers will see the current valuation and its methodology.'
          : 'Valuation is hidden. Advisors can request it from you by email.'}
      </p>
    </div>
  );
};

export default ProvenanceShareCard;
